<template>
  <Teleport to="body">
    <div
      class="tags-panel ignore-fix-focus l11-waap"
      v-if="selectedTag"
      :style="`--header-main-height: ${headerMainHeight}px`"
      ref="tagsPanelRef"
      tabindex="0"
    >
      <div class="tags-panel-header">
        <span
          class="tag-name rbz-text-h4 ellipsis"
          ref="tagNameRef"
          v-b-tooltip.bottom="tagNameTooltip"
        >
          {{ selectedTag }}
        </span>
        <rbz-form-button
          variant="tertiary"
          icon="IconClose"
          :icon-props="{ color: 'dusty-gray' }"
          @button-click="closePanel()"
          :ignore-update-in-process="true"
          class="close-panel-button"
          data-qa="close-panel-button"
        />
      </div>
      <div class="tags-panel-content">
        <rbz-loader :loading="!!isLoading" :data-exists="!!selectedTag" height="100%" />
        <div class="embeddings-table">
          <rbz-table
            :columns="columns"
            :table-data="selectedTagData.embeddings"
            :row-buttons="rowButtons"
            @open-new-row-button-clicked="handleRowButtonClicked"
            :show-new-button="false"
            data-qa="embeddings-table"
            @download-data="downloadEmbeddings"
            @click="
              () => {
                skipNextCloseListener = true;
              }
            "
          >
            <template #tableResultsOverride>
              <span class="embeddings-table-title rbz-text-regular">
                {{ t('tags.panel.securityElements.title') }}
              </span>
            </template>
            <template #tagUseCell="cellProps">
              <div class="tag-use-cell">
                <icon-tag-use-setter v-if="cellProps.row.field?.id === 'tags'" />
                <icon-tag-use-getter v-else />
                <span class="ellipsis">
                  {{
                    t(`tags.panel.fields.${Utils.snakeCaseToCamelCase(cellProps.row.field?.id)}`)
                  }}
                </span>
              </div>
            </template>
          </rbz-table>
        </div>
      </div>
      <div class="tags-panel-footer">
        <rbz-form-button
          v-if="!isCurrentPageEventsLog"
          variant="tertiary"
          icon="IconLink"
          data-qa="events-log-link-button"
          :label="t('tags.panel.openEventsLog')"
          @button-click="closePanel()"
          :link="getEventsLogPath()"
        />
        <rbz-form-button
          variant="secondary"
          :label="t('tags.panel.close')"
          data-qa="close-panel-button"
          @button-click="closePanel()"
          :ignore-update-in-process="true"
        />
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import RbzFormButton from '@/components/RbzFormButton.vue';
import { useTagsStore } from '@/store/TagsStore';
import { computed, nextTick, ref, watch } from 'vue';
import RequestsUtils from '@/helpers/RequestsUtils';
import { shouldUseMockData, getMockTagData } from '@/helpers/MockDataUtils';
import { AxiosError, AxiosResponse } from 'axios';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import { useBranchesStore } from '@/store/BranchesStore';
import RbzLoader from '@/components/RbzLoader.vue';
import _ from 'lodash';
import { useRoute } from 'vue-router';
import IconTagUseSetter from 'ui-component-library/assets/icons/IconTagUseSetter.vue';
import IconTagUseGetter from 'ui-component-library/assets/icons/IconTagUseGetter.vue';
import Utils from 'ui-component-library/helpers/Utils';
import { useI18n } from 'vue-i18n';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import { TagEmbedding, TagData, CustomMouseEvent } from '@/types';

const { t } = useI18n();

interface TagsPanel {
  headerMainHeight?: number;
}

defineProps<TagsPanel>();

const branchesStore = useBranchesStore();
const tagsStore = useTagsStore();
const route = useRoute();
const tagsPanelRef = ref(null);

const emptyTagData: TagData = {
  type: null,
  embeddings: [],
};
const selectedTagData = ref<TagData>(_.cloneDeep(emptyTagData));
const columns = ref<RbzTableColumnOptions[]>([
  {
    id: 'document',
    title: t('tags.panel.securityElements.type'),
    fieldNames: ['document'],
    displayFunction: (entry) => {
      return entry.document?.title || '';
    },
    isSortable: true,
    isSearchable: true,
    cellContentClasses: 'ellipsis',
  },
  {
    id: 'entity',
    title: t('tags.panel.securityElements.name'),
    fieldNames: ['entity'],
    displayFunction: (entry) => {
      return entry.entity?.name || '';
    },
    isSortable: true,
    isSearchable: true,
    classes: 'width-200px',
    cellContentClasses: 'ellipsis',
  },
  {
    id: 'field',
    title: t('tags.panel.securityElements.tagUse'),
    fieldNames: ['field'],
    displaySlotName: 'tagUseCell',
    tooltipFunction: (entry) => {
      const fieldId = entry.field?.id;
      return t(`tags.panel.fields.${Utils.snakeCaseToCamelCase(fieldId)}`) || '';
    },
    displayFunction: (entry) => {
      const fieldId = entry.field?.id;
      return t(`tags.panel.fields.${Utils.snakeCaseToCamelCase(fieldId)}`) || '';
    },
    sortDisplayFunction: (entry) => {
      const fieldId = entry.field?.id;
      return t(`tags.panel.fields.${Utils.snakeCaseToCamelCase(fieldId)}`) || '';
    },
    isSortable: true,
    isSearchable: true,
    classes: 'width-140px',
  },
]);

const handleRowButtonClicked = (row: TagEmbedding) => {
  tagsStore.setEditorDocId(row.entity.id);
  tagsStore.setEditorDocType(row.document.type);
  closePanel();
};
const rowButtons = ref<RbzTableRowButton[]>([
  {
    id: 'openNew',
    icon: 'IconOpenNew',
    tooltipCallback: () => {
      return t('tags.panel.securityElements.linkTooltip');
    },
  },
]);
const loadingCounter = ref(null);
let axiosAbortController = new AbortController();
let clickListenerListenerActive = false;
let skipNextCloseListener = false as boolean;

const isLoading = computed((): boolean => {
  return loadingCounter.value === null || loadingCounter.value > 0;
});

const isCurrentPageEventsLog = computed((): boolean => {
  return route.name.toString().includes('Events Log');
});

const selectedTag = computed((): string | null => {
  return tagsStore.selectedTag;
});

watch(selectedTag, (value, oldValue) => {
  if (value && !clickListenerListenerActive) {
    openPanel();
  }
  if (value && oldValue && value !== oldValue) {
    skipNextCloseListener = true;
  }
  if (value) {
    loadTagData();
  }
});

const openPanel = () => {
  // set immediate to avoid closing the panel in the same event that opened it
  nextTick(() => {
    document.addEventListener('click', clickListener);
    document.addEventListener('keydown', escapeListener);
    clickListenerListenerActive = true;
    tagsPanelRef.value.focus();
    tagsPanelRef.value.blur();
  });
};

const closePanel = () => {
  tagsStore.selectedTag = null;
  document.removeEventListener('click', clickListener);
  document.removeEventListener('keydown', escapeListener);
  clickListenerListenerActive = false;
};

const setLoadingStatus = (isLoading: boolean) => {
  if (isLoading) {
    loadingCounter.value++;
  } else {
    loadingCounter.value--;
  }
};

const loadTagData = () => {
  const tag = selectedTag.value;
  if (!tag) {
    return;
  }
  setLoadingStatus(true);
  selectedTagData.value = _.cloneDeep(emptyTagData);
  if (shouldUseMockData()) {
    const data = getMockTagData(tag);
    selectedTagData.value.type = data.type || [];
    selectedTagData.value.embeddings = data.embeddings || [];
    _.forEach(selectedTagData.value.embeddings, (embeddingValue) => {
      embeddingValue.id = DatasetsUtils.generateUUID2();
    });
    setLoadingStatus(false);
  } else {
    axiosAbortController.abort('Operation cancelled because a new search has begun');
    axiosAbortController = new AbortController();
    const config = {
      signal: axiosAbortController.signal,
    };
    RequestsUtils.sendRequest({
      methodName: 'GET',
      url: `conf/${branchesStore.selectedBranchId}/tags/${tag}`,
      config: config,
    })
      .then((response: AxiosResponse) => {
        if (response) {
          selectedTagData.value.type = response.data?.type || [];
          selectedTagData.value.embeddings = response.data?.embeddings || [];
          _.forEach(selectedTagData.value.embeddings, (embeddingValue) => {
            embeddingValue.id = DatasetsUtils.generateUUID2();
          });
        }
      })
      .catch((err: AxiosError) => {
        console.log('Error while attempting to load tag data');
        console.log(err);
      })
      .finally(() => {
        setLoadingStatus(false);
      });
  }
};

const getEventsLogPath = () => {
  const query = encodeURIComponent(`tags="${selectedTag.value}"`);
  return `/${branchesStore.selectedBranchId}/events-log${query ? `?filters=${query}` : ''}`;
};

const clickListener = (event: CustomMouseEvent) => {
  const customTagClick = event.elementsClicked?.includes('CustomTag');
  const customTagRemoveClick = event.elementsClicked?.includes('CustomTagRemoveButton');
  const customTagDoubleClick = event.elementsClicked?.includes('CustomTagDoubleClick');
  if (
    !tagsPanelRef.value?.contains(event.target) &&
    !skipNextCloseListener &&
    (!customTagClick || (customTagClick && (customTagRemoveClick || customTagDoubleClick)))
  ) {
    closePanel();
  }
  skipNextCloseListener = false;
};

const escapeListener = (event: KeyboardEvent) => {
  if (event.code === 'Escape') {
    closePanel();
    event.stopPropagation();
  }
};

const downloadEmbeddings = () => {
  Utils.downloadFile('security-elements', 'json', selectedTagData.value?.embeddings);
};

const tagNameRef = ref<HTMLElement | null>(null);
const tagNameTooltip = computed(() => {
  return Utils.elementTreeOverflow(tagNameRef.value) ? (selectedTag.value ?? '') : '';
});
</script>

<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

$tags-panel-header-height: 80px;
$tags-panel-footer-height: 92px;

.tags-panel {
  background: map.get(colors.$colors, 'alabaster');
  box-shadow:
    0 0 1px rgba(9, 30, 66, 0.31),
    0 3px 5px 0 rgba(9, 30, 66, 0.2);
  display: flex;
  flex-direction: column;
  height: calc(100% - var(--header-main-height));
  position: fixed;
  right: 0;
  top: var(--header-main-height);
  width: 640px;
  z-index: 90060;

  &:focus {
    outline: none;
  }
}

.tags-panel-header {
  height: $tags-panel-header-height;
  padding: 28px 24px 12px;

  .tag-name {
    max-width: 500px;
    width: fit-content;
  }

  .close-panel-button {
    position: absolute;
    right: 10px;
    top: 10px;
  }

  :deep(.close-panel-button .rbz-button:not(:hover):not(:active):not(:focus)) {
    background: transparent;
  }
}

.tags-panel-content {
  flex-grow: 1;
  margin: 12px 0;
  max-height: calc(100% - $tags-panel-header-height - $tags-panel-footer-height);
  overflow: auto;
  padding: 0 24px;

  .embeddings-table {
    background: map.get(colors.$colors, 'white');
  }

  :deep(.tag-use-cell) {
    align-items: center;
    display: flex;
    gap: 4px;
  }
}

.tags-panel-footer {
  display: flex;
  gap: 10px;
  height: $tags-panel-footer-height;
  justify-content: flex-end;
  padding: 16px 32px 40px;

  :deep(.rbz-button:not(:hover):not(:active):not(:focus)) {
    background-color: transparent;
  }
}

:deep(.rbz-loader) {
  background: map.get(colors.$colors, 'alabaster');
}
</style>
